import React, { useEffect, useState, useRef } from 'react';
import OnboardingModal from './OnboardingModal';
import { api } from '../utils/api';

const PUBLIC_KEY = process.env.REACT_APP_VAPI_PUBLIC_KEY || '1982777e-4159-4b67-981d-4a99ae5faf31';

// Vapi assistant IDs are UUIDs. Reject mock IDs (mock_vapi_...) and internal ids (agent_...).
const VAPI_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isValidVapiAssistantId(id) {
  return typeof id === 'string' && id.length > 0 && VAPI_UUID_REGEX.test(id) && !id.startsWith('mock_');
}

export default function VoiceAgentSettings({ showToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', greeting: '', voice: 'alloy' });
  const [callStatus, setCallStatus] = useState('idle');
  const [vapiLoaded, setVapiLoaded] = useState(false);
  const [agentData, setAgentData] = useState(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null); // 'ok' | 'mock' | 'missing' | null (unknown)
  
  const vapiRef = useRef(null);

  // Get Vapi assistant ID only (never internal agent.id). Used for starting calls.
  const getVapiAssistantId = () => {
    const storedAgentData = localStorage.getItem('agentData');
    const storedUser = localStorage.getItem('user');
    let vapiId = null;
    if (storedAgentData) {
      try {
        const data = JSON.parse(storedAgentData);
        vapiId = data.vapiAssistantId || data.demo_assistant_id || null;
      } catch (err) {
        console.error('Error parsing stored agent data:', err);
      }
    }
    if (!vapiId && storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.agent) {
          vapiId = user.agent.vapiAssistantId || user.agent.demo_assistant_id || null;
        }
      } catch (err) {
        console.error('Error parsing stored user data:', err);
      }
    }
    return vapiId;
  };

  // Load agent data
  useEffect(() => {
    const loadAgentData = async () => {
      try {
        setLoading(true);
        
        // Get agent data from localStorage
        const storedAgentData = localStorage.getItem('agentData');
        const storedUser = localStorage.getItem('user');
        
        let agent = null;
        if (storedAgentData) {
          agent = JSON.parse(storedAgentData);
        } else if (storedUser) {
          const user = JSON.parse(storedUser);
          agent = user.agent;
        }
        
        // Fallback: if local storage is stale or missing, hydrate from backend profile.
        if (!agent) {
          try {
            const me = await api('/api/onboarding/me');
            const serverAgent =
              me?.agent ||
              (me?.demo_assistant_id || me?.vapiAssistantId || me?.agentName
                ? {
                    agentName: me.agentName || '',
                    firstMessage: me.firstMessage || '',
                    agentVoice: me.agentVoice || 'alloy',
                    vapiAssistantId: me.vapiAssistantId || me.demo_assistant_id || null,
                  }
                : null);
            if (serverAgent) {
              agent = serverAgent;
              localStorage.setItem('agentData', JSON.stringify(serverAgent));
            }
          } catch {
            // Keep existing behavior if backend is unavailable.
          }
        }

        if (agent) {
          setAgentData(agent);
          setForm({
            name: agent.agentName || '',
            greeting: agent.firstMessage || '',
            voice: agent.agentVoice || 'alloy'
          });
          const vapiId = agent.vapiAssistantId || agent.demo_assistant_id;
          setConnectionStatus(
            !vapiId ? 'missing' : isValidVapiAssistantId(vapiId) ? 'ok' : 'mock'
          );
          if (vapiId && isValidVapiAssistantId(vapiId)) await loadAgentFromVapi();
        } else {
          setConnectionStatus('missing');
        }
      } catch (error) {
        console.error('Error loading agent data:', error);
        setError('Failed to load agent data');
      } finally {
        setLoading(false);
      }
    };
    
    loadAgentData();
  }, []);

  const loadAgentFromVapi = async () => {
    try {
      const me = await api('/api/onboarding/me');
      if (me.agentName || me.configuration_json) {
        setForm((f) => ({
          ...f,
          name: me.agentName || me.configuration_json?.agentName || f.name,
          greeting: f.greeting
        }));
      }
    } catch {
      /* offline */
    }
  };

  // Initialize Vapi
  useEffect(() => {
    const initVapi = async () => {
      try {
        const Vapi = (await import('@vapi-ai/web')).default;
        vapiRef.current = new Vapi(PUBLIC_KEY);
        
        vapiRef.current.on('call-start', () => {
          setCallStatus('active');
          showToast('Call started!');
        });
        
        vapiRef.current.on('call-end', () => {
          setCallStatus('idle');
          showToast('Call ended');
        });
        
        vapiRef.current.on('error', (err) => {
          console.error('Vapi error event:', err);
          setCallStatus('idle');
          const msg = err?.message || err?.error || (typeof err === 'string' ? err : 'Voice error');
          showToast(msg || 'Failed to start call. Please try again.');
        });
        
        vapiRef.current.on('call-start-failed', (event) => {
          console.error('Vapi call-start-failed:', event);
          setCallStatus('idle');
          const msg = event?.error || event?.stage || 'Call failed to start';
          showToast(msg || 'Failed to start call. Please try again.');
        });
        
        setVapiLoaded(true);
        console.log('✅ Vapi initialized successfully');
      } catch (error) {
        console.error('Error initializing Vapi:', error);
        setError('Failed to initialize voice system');
      }
    };
    
    initVapi();
  }, [showToast]);

  // Start call (only with valid Vapi assistant UUID)
  const startCall = async () => {
    const assistantId = getVapiAssistantId();
    if (!assistantId) {
      showToast('No assistant configured. Please set up your assistant first.');
      return;
    }
    if (!isValidVapiAssistantId(assistantId)) {
      showToast('Demo agent is not ready yet. Please refresh the page or try again in a moment.');
      return;
    }
    if (!vapiRef.current) {
      showToast('Voice system not ready. Please try again.');
      return;
    }
    try {
      setCallStatus('starting');
      await vapiRef.current.start(assistantId);
    } catch (error) {
      console.error('Error starting call:', error);
      setCallStatus('idle');
      const msg = error?.message || error?.error || (error?.response?.data?.message || 'Failed to start call. Please try again.');
      showToast(typeof msg === 'string' ? msg : 'Failed to start call. Please try again.');
    }
  };

  // End call: stop what we can, then refresh the page so the connection is fully torn down.
  const endCall = () => {
    try {
      if (vapiRef.current) {
        vapiRef.current.stop();
      }
    } catch (_) {}
    showToast('Ending call...');
    window.location.reload();
  };

  // Save settings
  const saveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      
      const assistantId = getVapiAssistantId();
      if (!assistantId || !isValidVapiAssistantId(assistantId)) {
        setError('No valid demo assistant configured');
        return;
      }
      
      await api('/api/onboarding/demo-assistant', {
        method: 'PATCH',
        body: JSON.stringify({
          agentName: form.name,
          firstMessage: form.greeting,
          voice: form.voice
        })
      });
      showToast('Settings saved successfully!');
      await loadAgentFromVapi();
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
          <div className="w-10 h-10 border-2 border-purple-500/40 border-t-purple-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Loading voice agent…</p>
        </div>
      </div>
    );
  }

  if (!agentData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Welcome to Appify.AI!</h2>
          <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.5)' }}>Let&apos;s set up your AI assistant to get started.</p>
          <button
            onClick={() => setShowOnboardingModal(true)}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
          >
            Set Up Assistant
          </button>
        </div>
        
        {/* Onboarding Modal */}
        <OnboardingModal 
          isOpen={showOnboardingModal} 
          onClose={() => setShowOnboardingModal(false)} 
          clientData={JSON.parse(localStorage.getItem('user') || '{}')}
          onComplete={(agentData) => {
            // Reload the component to show the assistant settings
            window.location.reload();
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      {/* Header */}
      <div className="px-4 md:px-6 py-4" style={{ backgroundColor: '#0d0d18', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-lg font-extrabold text-white tracking-tight">Voice Agent</h1>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Test your AI agent and manage voice settings</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'ok' && vapiLoaded ? 'bg-green-400' :
                connectionStatus === 'mock' ? 'bg-amber-400' : 'bg-red-400'
              }`} />
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {!vapiLoaded ? 'Voice loading…' :
                 connectionStatus === 'ok' ? 'Demo agent ready' :
                 connectionStatus === 'mock' ? 'Demo not ready — retry or contact support' : 'Voice ready'}
              </span>
            </div>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="w-full sm:w-auto px-4 py-2 rounded-xl text-sm font-semibold text-white transition hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
            >
              Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6">
        {/* Voice Control - Beautiful Design */}
        <div className="rounded-2xl p-5 md:p-8 mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
          <div className="text-center">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Voice Agent Control</h2>
            <p className="text-gray-300 mb-8">Test your AI agent with a live call</p>
            
            {/* Beautiful Mic Button - Matching Marketing Page */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                {/* Decorative rings - pointer-events-none so they don't block button clicks */}
                <div className="absolute inset-0 rounded-full border-2 border-purple-500/40 animate-pulse pointer-events-none" aria-hidden="true" style={{ animationDuration: '2s' }} />
                <div className="absolute -inset-3 rounded-full border border-purple-500/20 animate-pulse pointer-events-none" aria-hidden="true" style={{ animationDelay: '0.7s', animationDuration: '2s' }} />
                
                {/* Main button - click to start call, click again to end call; z-10 so it's above rings */}
                <button
                  type="button"
                  onClick={callStatus === 'active' ? endCall : startCall}
                  disabled={
                    callStatus === 'starting' ||
                    (callStatus === 'idle' && (!vapiLoaded || connectionStatus !== 'ok'))
                  }
                  title={
                    callStatus === 'active'
                      ? 'Click to end call'
                      : connectionStatus !== 'ok'
                        ? 'Complete demo setup first — then the mic will be clickable'
                        : !vapiLoaded
                          ? 'Voice system loading...'
                          : callStatus === 'starting'
                            ? 'Starting...'
                            : 'Click to start call'
                  }
                  className="relative z-10 w-32 h-32 rounded-full flex items-center justify-center transition-all transform shadow-2xl"
                  style={
                    callStatus === 'active'
                      ? { background: 'linear-gradient(135deg, #dc2626, #b91c1c)', cursor: 'pointer' }
                      : callStatus === 'idle' && vapiLoaded && connectionStatus === 'ok'
                        ? { background: 'linear-gradient(135deg, #8B5CF6, #C084FC)', cursor: 'pointer' }
                        : { backgroundColor: 'rgba(255,255,255,0.1)', cursor: 'default' }
                  }
                >
                  {callStatus === 'starting' ? (
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : callStatus === 'active' ? (
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 6h12v12H6z"/>
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            
            {/* Status text - Matching Marketing Page */}
            <div className="text-center">
              <p className={`text-lg font-semibold mb-2 ${
                callStatus === 'idle' ? 'text-green-400' : 
                callStatus === 'starting' ? 'text-yellow-400' : 
                callStatus === 'active' ? 'text-red-300' : 'text-gray-400'
              }`}>
                {callStatus === 'idle' ? 'Click to start call' : 
                 callStatus === 'starting' ? 'Starting call...' : 
                 callStatus === 'active' ? 'Click to end call' : 'Ready to call'}
              </p>
              <p className="text-gray-400 text-sm">
                {!vapiLoaded ? 'Initializing voice system...' :
                 connectionStatus === 'ok' ? 'Click the mic to test your demo agent' :
                 connectionStatus === 'mock' ? 'Demo agent could not be created; refresh or contact support' : 'Voice system ready'}
              </p>
            </div>
          </div>
        </div>

        {/* Agent Settings */}
        <div className="rounded-2xl p-6" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-base font-bold text-white mb-1">Agent Settings</h2>
          <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>Configure your AI assistant&apos;s voice and behavior</p>
          
          {error && (
            <div className="rounded-xl px-4 py-3 mb-5" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '0.5px solid rgba(239,68,68,0.25)' }}>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          <div className="space-y-6">
            {/* Assistant Name */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Assistant Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                placeholder="Enter assistant name"
              />
            </div>

            {/* First Message */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>First Message</label>
              <textarea
                value={form.greeting}
                onChange={(e) => setForm({...form, greeting: e.target.value})}
                rows={3}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition resize-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                placeholder="Enter the first message your assistant will say"
              />
            </div>

            {/* Voice Selection */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Voice</label>
              <select
                value={form.voice}
                onChange={(e) => setForm({...form, voice: e.target.value})}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
              >
                <option value="alloy">Alloy (Neutral)</option>
                <option value="echo">Echo (Male)</option>
                <option value="fable">Fable (British)</option>
                <option value="onyx">Onyx (Deep)</option>
                <option value="nova">Nova (Female)</option>
                <option value="shimmer">Shimmer (Soft)</option>
              </select>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={saveSettings}
                disabled={saving}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}